import { forwardRef } from 'react';

import {
  DrawerProps,
  Icons,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  LinkBehaviour,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import { Cluster, Node } from '@cast/types';

import { CopyToClipboard } from 'components/clickable-actions';
import { Link } from 'components/router';

import { NodeDetailsDrawerContent } from './NodeDetailsDrawerContent';
import { isTestNode } from '../../../_utils/common';
import { NodeDetailsDrawerActions } from '../../../components/NodeDetailsDrawerActions';
import { TestNodeChip } from '../../../components/TestNodeChip';
import { makeNodeDetailsLink } from '../../../utils/link';

type Props = DrawerProps & {
  cluster: Cluster;
  node: Node;
  masterNodesCount: number;
};

export const NodeDetailsDrawer = forwardRef<HTMLDivElement, Props>(
  ({ node, cluster, masterNodesCount, ...props }: Props, ref) => {
    return (
      <Drawer ref={ref} {...props} size="small" testId="node-details-drawer">
        <DrawerHeader justifyContent="space-between">
          <NodeDetailsDrawerActions
            cluster={cluster}
            node={node}
            masterNodesCount={masterNodesCount}
          />
        </DrawerHeader>
        <DrawerBody>
          <DrawerContent>
            <Stack>
              <Stack direction="row" alignItems="center" gap={8}>
                <Typography variant="L12R">Node:</Typography>
                {isTestNode(node) && <TestNodeChip size="md" />}
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                gap={8}
                data-testid="node-name"
              >
                <LinkBehaviour
                  component={Link}
                  target="_blank"
                  to={makeNodeDetailsLink({
                    clusterId: cluster.id,
                    nodeId: node.id,
                  })}
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                    textDecoration: 'none',
                  }}
                >
                  <Typography variant="h5" component="span">
                    {node.name}

                    <Box
                      component="span"
                      sx={{
                        ml: 2,
                        verticalAlign: 'middle',
                      }}
                    >
                      <Icons.ArrowSquareOut size={20} weight="bold" />
                    </Box>
                  </Typography>
                </LinkBehaviour>
                <CopyToClipboard text={node.name || ''} iconSize={20} />
              </Stack>
            </Stack>
            <NodeDetailsDrawerContent clusterId={cluster.id} node={node} />
          </DrawerContent>
        </DrawerBody>
      </Drawer>
    );
  }
);

NodeDetailsDrawer.displayName = 'NodeDetailsDrawer';
